import React, { useRef, useState } from 'react'
import { CRMWorkflow, RedtailWorkflow, WealthboxWorkflow, WealthboxWorkflowStep } from '../../../../../../models/Integration'
import { User } from '../../../../../../models/User'
import { TrackEvent, useTracking } from '../../../common/tracking'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useUpdateIntegrationEntityMutation } from '../../../../../../store/integrations/integrations'
import classNames from 'classnames'
import { Button } from '../../../../../../components/Button/Button'
import { CompleteWealthboxWorkflowStepOverlay } from './wealthbox/CompleteWealthboxWorkflowOverlay'
import { CompleteRedtailWorkflowStepOverlay } from './redtail/CompleteRedtailWorkflowOverlay'

export const CRMWorkflowView: React.FC<{
  workflow: CRMWorkflow
  contact: User
  serviceId: string
  onWorkflowStepCompleted: (wor: CRMWorkflow) => void
}> = ({ workflow, contact, serviceId, onWorkflowStepCompleted }) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const target = useRef(null)
  const containerRef = useRef(null)
  const [show, setShow] = useState(false)
  const needAdditionalInfoForComplete = !!(
    workflow.activeStep &&
    (('workflowOutcomes' in workflow.activeStep && (workflow.activeStep as WealthboxWorkflowStep).workflowOutcomes.length) ||
      ('workflowStepOutcomes' in workflow.activeStep && workflow.activeStep.workflowStepOutcomes.length))
  )
  const [triggerUpdateIntegrationEntity, { isLoading: isUpdatingWorkflow }] = useUpdateIntegrationEntityMutation()

  const _handleMarkAsCompleted = async () => {
    if (!workflow.activeStep) {
      return
    }
    const { data: updatedWorkflow } = await triggerUpdateIntegrationEntity({
      accountId,
      relatedTo: { id: '' },
      connector: serviceId,
      entityType: 'workflow_steps',
      entityId: `${workflow.id}-${workflow.activeStep.id}`,
      payload: { complete: true },
    }).unwrap()
    onWorkflowStepCompleted({
      id: updatedWorkflow.id,
      activeStep: updatedWorkflow.activeStep,
      name: updatedWorkflow.name,
      description: updatedWorkflow.description,
      label: updatedWorkflow.label,
    } as WealthboxWorkflow)
  }

  const handleMarkAsCompleted = tracked(_handleMarkAsCompleted, {
    eventName: TrackEvent.UPDATE_WORKFLOW_STEP_IN_CRM,
    eventDetails: {
      connector: serviceId,
      contactId: contact.id,
    },
  })

  const handleAdditionalInfoNeeded = () => {
    setShow(true)
  }

  const handleWorkflowStepCompleted = (workflow: CRMWorkflow) => {
    setShow(false)
    onWorkflowStepCompleted(workflow)
  }

  const getLabel = (workflow: CRMWorkflow) => {
    if ('label' in workflow) {
      return workflow.label
    }
    return null
  }

  return (
    <div className='d-flex gap-4 align-items-center border-default zocks-border-0.25 p-3'>
      <div className='d-flex flex-column gap-1'>
        <div className='d-flex gap-1'>
          <div className='bold'>{workflow.name}</div>
        </div>
        <div className={classNames('d-flex gap-1 paragraph-extra-small', { 'd-none': !getLabel(workflow) })}>
          <div className='align-self-end'>Label:</div>
          <div className='align-self-end bold'>{getLabel(workflow)}</div>
        </div>
      </div>
      <div>{workflow.activeStep?.name}</div>
      <div ref={containerRef} className='d-flex ms-auto justify-content-center align-items-center'>
        <Button
          ref={target}
          variant='borderless'
          className='button-small'
          onClick={needAdditionalInfoForComplete ? handleAdditionalInfoNeeded : handleMarkAsCompleted}
          loading={isUpdatingWorkflow}
        >
          <i className='bi bi-check' />
          <span className='text-decoration-underline'>Mark step as completed</span>
        </Button>
        {serviceId === 'wealthbox' ? (
          <CompleteWealthboxWorkflowStepOverlay
            targetRef={target}
            containerRef={containerRef}
            show={show}
            workflow={workflow as WealthboxWorkflow}
            serviceId={serviceId}
            onWorkflowStepCompleted={handleWorkflowStepCompleted}
            contact={contact}
            onHide={() => setShow(false)}
          />
        ) : null}
        {serviceId === 'redtail' ? (
          <CompleteRedtailWorkflowStepOverlay
            targetRef={target}
            containerRef={containerRef}
            show={show}
            workflow={workflow as RedtailWorkflow}
            serviceId={serviceId}
            onWorkflowStepCompleted={handleWorkflowStepCompleted}
            contact={contact}
            onHide={() => setShow(false)}
          />
        ) : null}
      </div>
    </div>
  )
}
